import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];

const DAYS_AR = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];
const DAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const GregorianDatePicker = ({ 
  value, 
  onChange, 
  label,
  className = '',
  minDate,
  maxDate,
  locale = 'ar' // 'ar' or 'en'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(null);
  const containerRef = useRef(null);

  const months = locale === 'ar' ? MONTHS_AR : MONTHS_EN;
  const days = locale === 'ar' ? DAYS_AR : DAYS_EN;

  // Parse the value (YYYY-MM-DD format)
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format for display (DD/MM/YYYY)
  const formatDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (value) {
      setViewDate(parseDate(value));
    } else {
      setViewDate(new Date());
    }
  }, [value]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedDate = value ? parseDate(value) : null;

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handlePrevYear = () => {
    setViewDate(new Date(viewDate.getFullYear() - 1, viewDate.getMonth(), 1));
  };

  const handleNextYear = () => {
    setViewDate(new Date(viewDate.getFullYear() + 1, viewDate.getMonth(), 1));
  };

  const handleDayClick = (day) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    
    // Check min/max bounds
    if (minDate && newDate < parseDate(minDate)) return;
    if (maxDate && newDate > parseDate(maxDate)) return;
    
    onChange(formatDate(newDate));
    setIsOpen(false);
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      viewDate.getFullYear() === today.getFullYear() &&
      viewDate.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      viewDate.getFullYear() === selectedDate.getFullYear() &&
      viewDate.getMonth() === selectedDate.getMonth() &&
      day === selectedDate.getDate()
    );
  };

  const isDisabled = (day) => {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    if (minDate && date < parseDate(minDate)) return true;
    if (maxDate && date > parseDate(maxDate)) return true;
    return false;
  };

  const renderCalendar = () => {
    if (!viewDate) return null;

    const daysInMonth = getDaysInMonth(viewDate);
    const firstDay = getFirstDayOfMonth(viewDate);
    const days_arr = [];

    // Empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days_arr.push(<div key={`empty-${i}`} className="w-9 h-9" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = isDisabled(day);
      days_arr.push(
        <button
          key={day}
          type="button"
          onClick={() => !disabled && handleDayClick(day)}
          disabled={disabled}
          className={`
            w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200
            ${isSelected(day) 
              ? 'bg-blue-600 text-white shadow-md' 
              : isToday(day) 
                ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                : disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
            }
          `}
        >
          {day}
        </button>
      );
    }

    return days_arr;
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      )}
      
      {/* Input Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl cursor-pointer bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 flex items-center justify-between"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value ? formatDisplay(value) : 'اختر التاريخ'}
        </span>
        <Calendar className="w-5 h-5 text-gray-400" />
      </div>

      {/* Calendar Dropdown */}
      {isOpen && viewDate && (
        <div className="absolute z-50 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-[320px]" style={{ left: 0 }}>
          {/* Header with Year Navigation */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
            <button
              type="button"
              onClick={handlePrevYear}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 flex items-center"
              title="السنة السابقة"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="font-bold text-gray-800 text-lg">
              {viewDate.getFullYear()}
            </span>
            <button
              type="button"
              onClick={handleNextYear}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 flex items-center"
              title="السنة التالية"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <span className="font-semibold text-gray-800">
              {months[viewDate.getMonth()]}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {days.map((day, idx) => (
              <div key={idx} className="w-9 h-8 flex items-center justify-center text-xs font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>

          {/* Today Button */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                onChange(formatDate(today));
                setViewDate(today);
                setIsOpen(false);
              }}
              className="w-full py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              اليوم
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GregorianDatePicker;
